import * as React from 'react';
import { Login as RaLogin, useLogin } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { Button as MuiButton, TextField as MuiTextField } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';

// ── Design tokens ──────────────────────────────────────────────────────────────
const TEAL       = '#3d7a7a';
const TEAL_DK    = '#2e6262';
const TEAL_FOCUS = 'rgba(61,122,122,0.10)';
const NAVY       = '#1e2a3a';
const INK        = '#1a2332';
const INK_2      = '#3a4454';
const MUTED      = '#7a8494';
const BORDER     = '#e5e8ed';
const FIELD_BG   = '#fafbfc';
const PAGE_BG    = '#f4f6f9';
const F_MUTED    = 'rgba(255,255,255,0.45)';

// ── Background preference ──────────────────────────────────────────────────────
const LS_BG_KEY = 'login_bg_pref';

type BgPreset = {
    id: string;
    label: string;
    css: string;
    isDark: boolean;
};

const BG_PRESETS: BgPreset[] = [
    { id: 'default',  label: 'Light',    css: PAGE_BG,                                                      isDark: false },
    { id: 'navy',     label: 'Navy',     css: 'linear-gradient(135deg, #0d1b2a 0%, #1e3a5f 100%)',          isDark: true  },
    { id: 'teal',     label: 'Teal',     css: 'linear-gradient(135deg, #0b4f4f 0%, #0d9488 100%)',          isDark: true  },
    { id: 'slate',    label: 'Slate',    css: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',          isDark: true  },
    { id: 'graphite', label: 'Dark',     css: 'linear-gradient(135deg, #111827 0%, #374151 100%)',          isDark: true  },
    { id: 'indigo',   label: 'Indigo',   css: 'linear-gradient(135deg, #312e81 0%, #6d28d9 100%)',          isDark: true  },
    { id: 'rose',     label: 'Rose',     css: 'linear-gradient(135deg, #881337 0%, #be123c 100%)',          isDark: true  },
    { id: 'forest',   label: 'Forest',   css: 'linear-gradient(135deg, #14532d 0%, #16a34a 100%)',          isDark: true  },
];

interface SavedBg {
    presetId: string;
    customUrl?: string;
}

function loadSavedBg(): SavedBg {
    try {
        const raw = localStorage.getItem(LS_BG_KEY);
        if (raw) return JSON.parse(raw) as SavedBg;
    } catch { /* ignore */ }
    return { presetId: 'default' };
}

function persistBg(b: SavedBg): void {
    try { localStorage.setItem(LS_BG_KEY, JSON.stringify(b)); } catch { /* ignore */ }
}

function resolveBgCss(saved: SavedBg): string {
    if (saved.presetId === 'custom' && saved.customUrl) {
        return `url(${saved.customUrl}) center / cover no-repeat`;
    }
    return BG_PRESETS.find(p => p.id === saved.presetId)?.css ?? PAGE_BG;
}

function resolveIsDark(saved: SavedBg): boolean {
    if (saved.presetId === 'custom' && saved.customUrl) return true;
    return BG_PRESETS.find(p => p.id === saved.presetId)?.isDark ?? false;
}

// ── Brand icon ─────────────────────────────────────────────────────────────────
const BrandIcon = ({ size = 14, color = '#fff' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
    </svg>
);

// ── Palette icon ───────────────────────────────────────────────────────────────
const PaletteIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor" />
        <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor" />
        <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor" />
        <circle cx="6.5" cy="12.5" r="0.5" fill="currentColor" />
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
);

// ── React-Admin sx overrides ───────────────────────────────────────────────────
const raLoginSx = {
    display: 'contents',

    '& .RaLogin-card': {
        // react-admin gives the card minWidth 300 — wider than our card's content on phones,
        // which pushed the fields and button past the right padding.
        width: '100%', maxWidth: '100%', minWidth: 0,
        m: 0, p: 0,
        boxShadow: 'none', borderRadius: 0, background: 'transparent',
    },
    backgroundImage: 'none !important',
    background:      'transparent !important',
    minHeight:       '0 !important',
    height:          'auto',

    '& .RaLogin-avatar':  { display: 'none !important' },
    '& .MuiAvatar-root':  { display: 'none !important' },
    '& .RaLogin-icon':    { display: 'none !important' },

    '& form': { display: 'flex', flexDirection: 'column', gap: '12px', p: 0 },
    '& .MuiFormControl-root': { width: '100%', mb: 0 },

    '& .MuiInputLabel-root': {
        position: 'static', transform: 'none',
        fontSize: '0.72rem', fontWeight: 600,
        color: INK_2, mb: '4px',
        display: 'block', lineHeight: 1.4, letterSpacing: '0.03em',
        '&.MuiInputLabel-shrink': { transform: 'none' },
    },
    '& legend': { display: 'none' },
    '& fieldset': { top: 0 },

    '& .MuiInputBase-root': {
        borderRadius: '7px',
        backgroundColor: FIELD_BG,
        fontSize: '0.85rem',
        height: '38px',
        transition: 'border-color 0.15s, background 0.15s',
        '&:hover':       { backgroundColor: '#fff' },
        '&.Mui-focused': { backgroundColor: '#fff' },
    },
    '& .MuiInputBase-input': { py: '8px', px: '12px' },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#dde2ea', borderWidth: '1.5px', borderRadius: '7px',
    },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#c2cad6',
    },
    '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: TEAL, borderWidth: '1.5px',
        boxShadow: `0 0 0 3px ${TEAL_FOCUS}`,
    },

    '& .RaLoginForm-button, & button[type="submit"]': {
        width: '100%', borderRadius: '7px',
        bgcolor: TEAL, color: '#fff',
        textTransform: 'none', fontWeight: 600,
        fontSize: '0.875rem', height: '40px',
        mt: '2px', boxShadow: 'none', letterSpacing: '0.01em',
        '&:hover': { bgcolor: TEAL_DK, boxShadow: 'none' },
    },
} as const;

// ── Page layout (classes, so it can respond to the screen size) ────────────────
// Desktop: one screen, no page scroll — the card area scrolls if it must. Phones and short
// screens: the page scrolls normally, the card fills the first screen and the footer follows
// below it instead of squeezing the card.
const LOGIN_CSS = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; }
    ::placeholder { color: #b4bcc8 !important; }

    .lg-root {
        height: 100dvh;
        display: flex; flex-direction: column;
        overflow: hidden;
        font-family: 'Plus Jakarta Sans', 'Inter', system-ui, sans-serif;
        background: ${PAGE_BG};
    }
    .lg-nav {
        flex-shrink: 0;
        background: #fff;
        border-bottom: 1px solid ${BORDER};
        height: 50px;
        display: flex; align-items: center; justify-content: space-between; gap: 12px;
        padding: 0 28px;
    }
    .lg-main {
        flex: 1; min-height: 0;
        display: flex; align-items: center; justify-content: center;
        padding: 12px 16px;
        overflow: auto;
        position: relative;
    }
    .lg-card { width: 100%; max-width: 370px; padding: 22px 28px 18px; }
    .lg-bg-panel { width: 288px; max-width: calc(100vw - 20px); }
    .lg-footer-connect {
        border-bottom: 1px solid rgba(255,255,255,0.08);
        padding: 10px 28px;
        display: flex; align-items: center; justify-content: space-between;
        flex-wrap: wrap; gap: 10px;
    }
    .lg-footer-grid {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
        padding: 12px 28px;
    }
    .lg-footer-bottom {
        border-top: 1px solid rgba(255,255,255,0.08);
        padding: 7px 68px 7px 28px; /* right: clear of the fixed WhatsApp button */
        display: flex; align-items: center; justify-content: space-between;
        flex-wrap: wrap; gap: 8px;
    }

    @media (max-width: 720px), (max-height: 640px) {
        .lg-root { height: auto; min-height: 100dvh; overflow: visible; }
        .lg-main {
            flex: 1 0 auto;
            min-height: calc(100dvh - 50px);
            overflow: visible;
            padding: 52px 16px 28px; /* room for the Background button above the card */
        }
    }
    @media (max-width: 720px) {
        .lg-nav { padding: 0 16px; }
        .lg-nav-links { display: none !important; }
        .lg-footer-connect, .lg-footer-grid { padding-left: 16px; padding-right: 16px; }
        .lg-footer-bottom { padding-left: 16px; }
        .lg-footer-grid { grid-template-columns: 1fr 1fr; gap: 16px; }
        /* 16px inputs: iOS Safari zooms the page into any smaller field on focus. */
        .lg-card .MuiInputBase-root { font-size: 16px !important; height: 44px !important; }
        .lg-card button[type="submit"] { height: 44px !important; }
    }
    @media (max-width: 480px) {
        .lg-nav-phone { display: none !important; }
        .lg-card { padding: 20px 18px 16px; }
        .lg-footer-grid { grid-template-columns: 1fr; }
    }
`;

// ── Login error → friendly message ──────────────────────────────────────────────
function resolveLoginError(err: unknown): string {
    const raw =
        err instanceof Error
            ? err.message
            : typeof err === 'string'
              ? err
              : err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string'
                ? (err as { message: string }).message
                : '';
    const m = raw.trim();
    if (/failed to fetch|networkerror|load failed|network request failed/i.test(m)) {
        return 'Cannot reach the server. Please check your connection and try again.';
    }
    if (m === '' || /invalid credentials|unauthor|401/i.test(m)) {
        return 'Incorrect username or password. Please try again.';
    }
    return m;
}

// ── Credentials form (react-admin auth + Toastify feedback) ──────────────────────
const CredentialsLoginForm = () => {
    const login = useLogin();
    const [username, setUsername] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;
        const user = username.trim();
        if (!user || !password) {
            toast.error('Please enter your username / email and password.');
            return;
        }
        setSubmitting(true);
        try {
            // On success react-admin handles the redirect to the app.
            await login({ username: user, password });
        } catch (err) {
            toast.error(resolveLoginError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={e => void onSubmit(e)} noValidate>
            <MuiTextField
                label="Username or email"
                value={username}
                onChange={e => setUsername(e.target.value)}
                fullWidth
                autoFocus
                autoComplete="username"
                disabled={submitting}
            />
            <MuiTextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                autoComplete="current-password"
                disabled={submitting}
            />
            <MuiButton type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
            </MuiButton>
        </form>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────
const Login = () => {
    const [savedBg, setSavedBg] = React.useState<SavedBg>(loadSavedBg);
    const [showPanel, setShowPanel] = React.useState(false);
    const [urlInput, setUrlInput] = React.useState<string>(() => loadSavedBg().customUrl ?? '');

    const bgCss  = resolveBgCss(savedBg);
    const isDark = resolveIsDark(savedBg);

    const selectPreset = (id: string) => {
        const next: SavedBg = { presetId: id, customUrl: savedBg.customUrl };
        setSavedBg(next);
        persistBg(next);
        setShowPanel(false);
    };

    const applyCustomUrl = () => {
        const url = urlInput.trim();
        if (!url) return;
        const next: SavedBg = { presetId: 'custom', customUrl: url };
        setSavedBg(next);
        persistBg(next);
        setShowPanel(false);
    };

    const clearCustom = () => {
        const next: SavedBg = { presetId: 'default' };
        setSavedBg(next);
        persistBg(next);
        setUrlInput('');
        setShowPanel(false);
    };

    const btnBg     = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.88)';
    const btnBorder = isDark ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(0,0,0,0.12)';
    const btnColor  = isDark ? 'rgba(255,255,255,0.9)' : INK;

    return (
        <div className="lg-root">

            <style>{LOGIN_CSS}</style>

            {/* ══════════════════════ NAVBAR ══════════════════════ */}
            <nav className="lg-nav">
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, minWidth: 0 }}>
                    <a href="#" style={{ display: 'flex', alignItems: 'center', gap: 7, textDecoration: 'none', flexShrink: 0 }}>
                        <div style={{
                            width: 27, height: 27, background: TEAL, borderRadius: 6,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <BrandIcon size={14} />
                        </div>
                        <span style={{ fontSize: 14.5, fontWeight: 700, color: INK, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                            Corbis Soft
                        </span>
                    </a>
                    <div className="lg-nav-links" style={{ display: 'flex', gap: 2 }}>
                        {['Home', 'Contact us'].map(l => <NavBtn key={l} label={l} />)}
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <span className="lg-nav-phone" style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        background: '#f0f2f5', borderRadius: 20,
                        padding: '3px 10px', fontSize: 12, color: MUTED, whiteSpace: 'nowrap',
                    }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                            stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="5" y="2" width="14" height="20" rx="2" />
                            <line x1="12" y1="18" x2="12.01" y2="18" />
                        </svg>
                        (+92) 300 8676368
                    </span>
                    <RouterLink to="/signup" style={{ textDecoration: 'none' }}>
                        <button style={{
                            background: TEAL, color: '#fff', border: 'none',
                            fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                            padding: '5px 14px', borderRadius: 6, cursor: 'pointer',
                        }}
                            onMouseEnter={e => (e.currentTarget.style.background = TEAL_DK)}
                            onMouseLeave={e => (e.currentTarget.style.background = TEAL)}
                        >Sign up</button>
                    </RouterLink>
                </div>
            </nav>

            {/* ══════════════════════ MAIN ══════════════════════ */}
            <main className="lg-main" style={{ background: bgCss }}>
                {/* Dark overlay for image / dark gradient backgrounds */}
                {isDark && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        background: 'rgba(0,0,0,0.22)',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }} />
                )}

                {/* ── Background customizer button ── */}
                <button
                    onClick={() => setShowPanel(p => !p)}
                    title="Change login background"
                    style={{
                        position: 'absolute', top: 10, right: 10, zIndex: 5,
                        background: btnBg,
                        border: btnBorder,
                        borderRadius: 7,
                        padding: '4px 10px',
                        cursor: 'pointer',
                        fontSize: 11.5, fontFamily: 'inherit', fontWeight: 600,
                        color: btnColor,
                        display: 'flex', alignItems: 'center', gap: 5,
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                    }}
                >
                    <PaletteIcon />
                    Background
                </button>

                {/* ── Customizer panel ── */}
                {showPanel && (
                    <div className="lg-bg-panel" style={{
                        position: 'absolute', top: 44, right: 10, zIndex: 10,
                        background: '#fff',
                        border: '1px solid #e5e8ed',
                        borderRadius: 12,
                        padding: '14px 16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: INK }}>Login Background</span>
                            <button
                                onClick={() => setShowPanel(false)}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: MUTED, padding: 0, fontSize: 18, lineHeight: 1,
                                    fontFamily: 'inherit',
                                }}
                            >×</button>
                        </div>

                        {/* Preset swatches */}
                        <p style={{ fontSize: 10, color: MUTED, marginBottom: 7, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700 }}>
                            Preset Themes
                        </p>
                        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 14 }}>
                            {BG_PRESETS.map(p => (
                                <div key={p.id} style={{ textAlign: 'center' }}>
                                    <button
                                        title={p.label}
                                        onClick={() => selectPreset(p.id)}
                                        style={{
                                            width: 44, height: 34,
                                            borderRadius: 6,
                                            background: p.css,
                                            border: savedBg.presetId === p.id
                                                ? `2.5px solid ${TEAL}`
                                                : '1.5px solid rgba(0,0,0,0.13)',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            padding: 0, outline: 'none',
                                            display: 'block',
                                        }}
                                    >
                                        {savedBg.presetId === p.id && (
                                            <span style={{
                                                position: 'absolute', inset: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: p.isDark ? '#fff' : TEAL,
                                                fontSize: 14, fontWeight: 700,
                                            }}>✓</span>
                                        )}
                                    </button>
                                    <span style={{ fontSize: 9.5, color: MUTED, display: 'block', marginTop: 3 }}>{p.label}</span>
                                </div>
                            ))}
                        </div>

                        {/* Divider */}
                        <div style={{ height: 1, background: BORDER, marginBottom: 12 }} />

                        {/* Custom image — upload or URL */}
                        <p style={{ fontSize: 10, color: MUTED, marginBottom: 7, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700 }}>
                            Custom Image
                        </p>

                        {/* Device upload removed: /api/uploads/login-bg now requires authentication,
                            and the login page is pre-auth by definition. Paste an image URL instead. */}

                        {/* URL input */}
                        <div style={{ display: 'flex', gap: 5 }}>
                            <input
                                value={urlInput}
                                onChange={e => setUrlInput(e.target.value)}
                                placeholder="https://example.com/bg.jpg"
                                onKeyDown={e => e.key === 'Enter' && applyCustomUrl()}
                                style={{
                                    flex: 1, padding: '6px 8px',
                                    border: '1px solid #dde2ea', borderRadius: 5,
                                    fontSize: 11.5, fontFamily: 'inherit',
                                    outline: 'none', color: INK,
                                    background: FIELD_BG,
                                }}
                            />
                            <button
                                onClick={applyCustomUrl}
                                style={{
                                    background: TEAL, color: '#fff', border: 'none',
                                    borderRadius: 5, padding: '6px 11px',
                                    fontSize: 11.5, cursor: 'pointer',
                                    fontFamily: 'inherit', fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                }}
                            >Set</button>
                        </div>
                        {savedBg.presetId === 'custom' && savedBg.customUrl ? (
                            <button
                                onClick={clearCustom}
                                style={{
                                    marginTop: 8, background: 'none', border: 'none',
                                    color: '#ef4444', cursor: 'pointer',
                                    fontSize: 11, fontFamily: 'inherit',
                                    padding: 0, fontWeight: 600,
                                }}
                            >Clear custom image</button>
                        ) : null}
                    </div>
                )}

                {/* ── Login card ── */}
                <div className="lg-card" style={{
                    background: '#fff',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : BORDER}`,
                    borderRadius: 12,
                    boxShadow: isDark
                        ? '0 4px 32px rgba(0,0,0,0.40)'
                        : '0 1px 4px rgba(0,0,0,0.06)',
                    position: 'relative', zIndex: 1,
                }}>
                    <h1 style={{
                        fontSize: 18, fontWeight: 700, color: INK,
                        letterSpacing: '-0.02em', marginBottom: 3,
                    }}>
                        Welcome back
                    </h1>
                    <p style={{ fontSize: 13, color: MUTED, marginBottom: 16, lineHeight: 1.45 }}>
                        Sign in to your account to continue
                    </p>

                    <RaLogin sx={raLoginSx}>
                        <CredentialsLoginForm />
                    </RaLogin>

                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginTop: 10,
                    }}>
                        <label style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            fontSize: 12, color: '#5a6474', cursor: 'pointer',
                        }}>
                            <input type="checkbox" style={{ accentColor: TEAL, width: 12, height: 12 }} />
                            Remember me
                        </label>
                        <a href="#" style={{ fontSize: 12, color: TEAL, textDecoration: 'none', fontWeight: 500 }}>
                            Forgot password?
                        </a>
                    </div>

                    <p style={{ marginTop: 12, textAlign: 'center', fontSize: 12, color: MUTED }}>
                        Don't have an account?{' '}
                        <RouterLink to="/signup" style={{ color: TEAL, textDecoration: 'none', fontWeight: 600 }}>
                            Sign up
                        </RouterLink>
                    </p>
                </div>
            </main>

            {/* ══════════════════════ FOOTER ══════════════════════ */}
            <footer style={{
                flexShrink: 0,
                background: NAVY,
                color: '#fff',
                overflowY: 'auto',
            }}>
                {/* Connect row */}
                <div className="lg-footer-connect">
                    <div>
                        <p style={{ fontSize: 12.5, fontWeight: 600, color: '#fff', marginBottom: 2 }}>
                            Connect with us
                        </p>
                        <p style={{ fontSize: 11.5, color: F_MUTED, marginBottom: 8 }}>
                            Your message will be delivered to our client support team.
                        </p>
                        <div style={{ display: 'flex', gap: 6 }}>
                            {[
                                { label: 'Facebook', href: 'https://facebook.com',       path: 'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z' },
                                { label: 'Twitter',  href: 'https://twitter.com',        path: 'M23 3a10.9 10.9 0 01-3.14 1.53A4.48 4.48 0 0012 8v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z' },
                                { label: 'LinkedIn', href: 'https://linkedin.com',       path: 'M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z' },
                                { label: 'WhatsApp', href: 'https://wa.me/923008676368', path: 'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z' },
                            ].map(s => <SocialBtn key={s.label} {...s} />)}
                        </div>
                    </div>
                    <a href="mailto:sales@corbissoft.com" style={{ textDecoration: 'none' }}>
                        <button style={{
                            background: 'none',
                            border: '1px solid rgba(255,255,255,0.28)',
                            color: '#fff', fontFamily: 'inherit',
                            fontSize: 12, fontWeight: 600,
                            padding: '6px 16px', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)'; }}
                        >Contact Us</button>
                    </a>
                </div>

                {/* 3-column grid */}
                <div className="lg-footer-grid">
                    <div>
                        <ColTitle>Useful Links</ColTitle>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {['Home', 'About us', 'Products', 'Services', 'Legal', 'Contact us'].map(l => (
                                <a key={l} href="#"
                                    style={{ fontSize: 12, color: F_MUTED, textDecoration: 'none' }}
                                    onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.85)')}
                                    onMouseLeave={e => (e.currentTarget.style.color = F_MUTED)}
                                >{l}</a>
                            ))}
                        </div>
                    </div>
                    <div>
                        <ColTitle>About us</ColTitle>
                        <p style={{ fontSize: 12, color: F_MUTED, lineHeight: 1.6 }}>
                            We are a team of passionate people whose goal is to improve everyone's life through disruptive products.
                        </p>
                    </div>
                    <div>
                        <ColTitle>Contact</ColTitle>
                        <FooterContact
                            icon={<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></>}
                            text="425-J3, Johar Town, Lahore"
                        />
                        <FooterContact
                            icon={<rect x="5" y="2" width="14" height="20" rx="2"/>}
                            text="(+92) 300 8676368"
                            href="tel:+923008676368"
                        />
                        <FooterContact
                            icon={<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>}
                            text="sales@corbissoft.com"
                            href="mailto:sales@corbissoft.com"
                        />
                    </div>
                </div>

                {/* Copyright bar */}
                <div className="lg-footer-bottom">
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)' }}>
                        Copyright © Corbis Soft — All Rights Reserved
                    </span>
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 3,
                            fontSize: 11, color: 'rgba(255,255,255,0.32)', fontFamily: 'inherit',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.65)')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.32)')}
                    >
                        Go to top
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="18 15 12 9 6 15" />
                        </svg>
                    </button>
                </div>
            </footer>

            {/* WhatsApp FAB */}
            <a href="https://wa.me/923008676368" target="_blank" rel="noreferrer" aria-label="WhatsApp"
                style={{
                    position: 'fixed', right: 14, bottom: 14, zIndex: 50,
                    width: 40, height: 40, background: '#25d366', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    textDecoration: 'none', boxShadow: '0 2px 8px rgba(37,211,102,0.38)',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#1ebe5d')}
                onMouseLeave={e => (e.currentTarget.style.background = '#25d366')}
            >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="#fff">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M11.997 2C6.477 2 2 6.477 2 12c0 1.797.471 3.48 1.29 4.943L2 22l5.233-1.264A9.954 9.954 0 0011.997 22C17.518 22 22 17.523 22 12S17.518 2 11.997 2z"/>
                </svg>
            </a>

            <ToastContainer
                position="top-right"
                autoClose={4000}
                newestOnTop
                closeOnClick
                pauseOnHover
                theme="light"
                limit={3}
            />
        </div>
    );
};

// ── Nav button ─────────────────────────────────────────────────────────────────
function NavBtn({ label }: { label: string }) {
    const [hov, setHov] = React.useState(false);
    return (
        <button style={{
            background: hov ? '#f0f2f5' : 'none', border: 'none',
            fontFamily: 'inherit', fontSize: 13, color: hov ? INK : '#5a6474',
            padding: '4px 9px', borderRadius: 5, cursor: 'pointer',
        }}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        >{label}</button>
    );
}

// ── Social button ──────────────────────────────────────────────────────────────
function SocialBtn({ href, label, path }: { href: string; label: string; path: string }) {
    const [hov, setHov] = React.useState(false);
    return (
        <a href={href} target="_blank" rel="noreferrer" aria-label={label} style={{
            width: 28, height: 28,
            border: `1px solid ${hov ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.20)'}`,
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', textDecoration: 'none',
            background: hov ? 'rgba(255,255,255,0.10)' : 'none',
        }}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d={path} />
            </svg>
        </a>
    );
}

// ── Footer column title ────────────────────────────────────────────────────────
function ColTitle({ children }: { children: React.ReactNode }) {
    return (
        <p style={{
            fontSize: 10.5, fontWeight: 700, color: '#fff',
            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8,
        }}>{children}</p>
    );
}

// ── Footer contact row ─────────────────────────────────────────────────────────
function FooterContact({ icon, text, href }: { icon: React.ReactNode; text: string; href?: string }) {
    const [hov, setHov] = React.useState(false);
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke={F_MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: 1 }}>
                {icon}
            </svg>
            {href
                ? <a href={href} style={{ fontSize: 12, color: hov ? 'rgba(255,255,255,0.85)' : F_MUTED, textDecoration: 'none' }}
                    onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>{text}</a>
                : <span style={{ fontSize: 12, color: F_MUTED }}>{text}</span>
            }
        </div>
    );
}

export default Login;
